// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MOCK_SOPS } from '../mockData';
import type { SOPStatus } from '../../../types';
import './SOPDetail.css';

function getStatusIcon(status: SOPStatus): string {
  switch (status) {
    case 'Active':
      return '\u2705';
    case 'Draft':
      return '\ud83d\udcdd';
    case 'Archived':
      return '\u23f8';
    case 'Pending Approval':
      return '\u23f3';
    default:
      return '';
  }
}

function SOPDetail() {
  const { site_id, sopId } = useParams();

  const sop = useMemo(() => {
    return MOCK_SOPS.find(s => s.id === sopId) ?? MOCK_SOPS[0];
  }, [sopId]);

  const statusIcon = getStatusIcon(sop.status);

  return (
    <div className="sop-detail-container">
      {/* Back navigation handled by breadcrumb in SiteDetailPage header */}

      <div className="sop-detail-header">
        <div className="sop-detail-header-left">
          <h2>{sop.name}</h2>
          <p className="sop-detail-meta">
            {sop.version} &middot; {statusIcon} {sop.status} &middot; Approved {sop.approvalDate} by{' '}
            {sop.approvedBy}
          </p>
        </div>
        <div className="sop-detail-actions">
          <Link
            to={`/site/${site_id}/sop/${sopId}/edit`}
            className="sop-action-btn sop-action-edit"
          >
            Edit
          </Link>
          <Link
            to={`/site/${site_id}/sop/${sopId}/versions`}
            className="sop-action-btn sop-action-versions"
          >
            Versions ({sop.versions.length})
          </Link>
        </div>
      </div>

      <div className="sop-detail-card">
        <h3 className="sop-detail-card-title">{'\ud83d\udccb'} Overview</h3>
        <p className="sop-detail-card-text">{sop.overview}</p>
      </div>

      <div className="sop-detail-card">
        <h3 className="sop-detail-card-title">{'\ud83d\udee1'} Safety Boundaries</h3>
        <table className="sop-detail-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Min</th>
              <th>Max</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            {sop.safetyBoundaries.map((row, i) => (
              <tr key={i}>
                <td>{row.parameter}</td>
                <td>{row.min}</td>
                <td>{row.max}</td>
                <td>{row.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sop-detail-card">
        <h3 className="sop-detail-card-title">{'\u2699'} Equipment Configuration</h3>
        <table className="sop-detail-table">
          <thead>
            <tr>
              <th>Equipment</th>
              <th>Parameter</th>
              <th>Normal</th>
              <th>Optimized</th>
            </tr>
          </thead>
          <tbody>
            {sop.equipmentConfig.map((row, i) => (
              <tr key={i}>
                <td>{row.equipment}</td>
                <td>{row.parameter}</td>
                <td>{row.normal}</td>
                <td>{row.optimized}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sop-detail-card">
        <h3 className="sop-detail-card-title">{'\u2705'} Pre-Execution Checks</h3>
        <ul className="sop-detail-checklist">
          {sop.preChecks.map((check, i) => (
            <li key={i}>
              <span className="sop-check-icon">{'\u2610'}</span> {check}
            </li>
          ))}
        </ul>
      </div>

      <div className="sop-detail-card">
        <h3 className="sop-detail-card-title">{'\u2705'} Post-Execution Checks</h3>
        <ul className="sop-detail-checklist">
          {sop.postChecks.map((check, i) => (
            <li key={i}>
              <span className="sop-check-icon">{'\u2610'}</span> {check}
            </li>
          ))}
        </ul>
      </div>

      <div className="sop-detail-card">
        <h3 className="sop-detail-card-title">{'\u21a9'} Rollback Instructions</h3>
        <ol className="sop-detail-rollback-list">
          {sop.rollbackInstructions.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </div>

      <div className="sop-detail-card">
        <h3 className="sop-detail-card-title">{'\ud83d\udcce'} Metadata</h3>
        <p className="sop-detail-card-text">
          Author: {sop.author} &middot; Created {sop.createdDate}
        </p>
        <p className="sop-detail-card-text">
          Linked executions: {sop.linkedExecutionCount} &middot; Last used: {sop.lastUsed}
        </p>
      </div>
    </div>
  );
}

export default SOPDetail;
